// Base edge schema
// Based on ADR-0005 (Base Interface Pattern for Type Hierarchies)

import { z } from 'zod';
import { EdgeDiagramPropertiesSchema, DesignStatusSchema, ValueWithUnitSchema, RgbaSchema } from './common';

/**
 * Base schema for all edges (pipes)
 * All edge types must extend this base
 * 
 * Enforces:
 * - Unique ID for graph operations (ADR-0003: Graph-Based Structure)
 * - Type discriminator for TypeScript narrowing (ADR-0008: Type Field Dual Purpose)
 * - Connection topology (source and target vertex IDs)
 * - Diagram properties for rendering pipe paths
 * - Display data (name)
 */
/**
 * Calculation results that can be stored on any edge.
 * Populated by the solver after a successful calculation run.
 */
export const EdgeCalcSchema = z.object({
  flow: ValueWithUnitSchema.optional(),
  pressure_drop: ValueWithUnitSchema.optional(),
});

export type EdgeCalc = z.infer<typeof EdgeCalcSchema>;

export const BaseEdgeSchema = z.object({
  id: z.string(),
  type: z.string(), // Will be overridden with literal by specific types

  // Display data
  diagram: EdgeDiagramPropertiesSchema,
  name: z.string(),

  // Calculation data - connection topology
  connection: z.object({
    source_id: z.string(),
    target_id: z.string(),
  }),

  // v2: common device fields from C++ device_design_record
  note: z.string().optional(),
  design_status: DesignStatusSchema.optional(),
  custom_field_values: z.record(
    z.string(),
    z.union([z.boolean(), z.number(), z.string()])
  ).optional(),
  layer_id: z.string().optional(),
  manually_routed: z.boolean().optional(),

  // v2: editable per-element color override (spec §5a). Connect-native — Desktop
  // stores none. When set it wins over the computed display color. Presentation-
  // only: stripped from the versioned structural diff so a recolor never churns
  // history (see backend model-diff stripCalc/stripColor).
  color: RgbaSchema.optional(),

  // Solver calculation results
  calc: EdgeCalcSchema.optional(),
});

export type BaseEdge = z.infer<typeof BaseEdgeSchema>;

